import React, { useEffect, useState, useMemo } from "react";
import {
  Paper, Grid, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, IconButton, Tooltip, CircularProgress, TextField, Button,
  Chip, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { makeStyles } from 'tss-react/mui';
import { alpha } from "@mui/material/styles";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendIcon from "@mui/icons-material/Send";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import ImageIcon from "@mui/icons-material/Image";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { red, green, orange, blue } from "@mui/material/colors";
import moment from "moment";
import "moment/locale/es";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import api from "../../services/api";
import SelectConection from "../../components/SelectConection";

moment.locale("es");

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    background: theme.palette.background.default,
  },
  cardsWrap: { marginTop: theme.spacing(1), marginBottom: theme.spacing(2) },
  card: {
    position: "relative",
    textAlign: "left",
    padding: theme.spacing(2),
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.palette.mode === "light"
      ? "0 6px 18px rgba(0,0,0,0.05)"
      : "0 6px 18px rgba(0,0,0,0.30)",
    minHeight: 96,
  },
  cardIcon: { position: "absolute", right: 12, top: 12, opacity: 0.22, fontSize: 48 },
  cardTitle: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.2,
    textTransform: "uppercase",
  },
  cardNumber: { marginTop: 6, fontSize: 28, fontWeight: 800 },
  filtersBar: {
    padding: theme.spacing(1.5),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
  },
  quickBtn: { textTransform: "none" },
  tablePaper: {
    marginTop: theme.spacing(2),
    borderRadius: 12,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },
  tableContainer: { maxHeight: 560 },
  headCell: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    background: theme.palette.mode === "light" ? "#f4f6f8" : theme.palette.background.default,
  },
  rowHover: { "&:hover": { backgroundColor: theme.palette.action.hover } },
  zebra: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.mode === "light" ? "#fafcff" : alpha(theme.palette.common.white, 0.02),
    },
  },
  stickyProgress: { display: "flex", alignItems: "center", justifyContent: "center", padding: theme.spacing(6) },
  emptyState: { textAlign: "center", padding: theme.spacing(6), color: theme.palette.text.secondary },
  statusChip: { fontWeight: 600 },
  priorityChip: { fontWeight: 600 },
}));

const WhatsappManager = () => {
  const { classes } = useStyles();
  const companyId = localStorage.getItem("companyId");

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState({ sent: 0, failed: 0, pending: 0, multimedia: 0, dataUsed: 0 });

  const [dateFrom, setDateFrom] = useState(moment().format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));

  const [filterWhatsapp, setFilterWhatsapp] = useState(null);
  const [filterNumber, setFilterNumber] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterPriority, setFilterPriority] = useState("ALL");
  const [filterQuery, setFilterQuery] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const openDetails = (row) => { setSelectedRow(row); setDetailOpen(true); };
  const closeDetails = () => { setDetailOpen(false); setSelectedRow(null); };

  const handleRetry = async (row) => {
    try {
      setRetryingId(row.id);
      await api.post("/whatsapp/manager/retry", { id: row.id });
      toast.success(i18n.t("whatsappManager.toasts.retrying"));
      await loadData();
    } catch (e) {
      toast.error(i18n.t("whatsappManager.toasts.retryError"));
    } finally {
      setRetryingId(null);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        companyId,
        dateFrom,
        dateTo,
        whatsappId: filterWhatsapp,
        number: (filterNumber || "").trim(),
        status: filterStatus === "ALL" ? "" : filterStatus,
        priority: filterPriority === "ALL" ? "" : filterPriority,
        query: (filterQuery || "").trim()
      };

      const { data } = await api.get("/whatsapp/analytics", { params });

      setSummary(data.summary || { sent: 0, failed: 0, pending: 0, multimedia: 0, dataUsed: 0 });

      const msgs = (data.messages || []).map((r) => ({
        id: r.id,
        whatsapp: r.whatsapp?.name || r.whatsapp || i18n.t("whatsappManager.detail.unknown"),
        number: r.number,
        status: r.status,
        priority: r.priority,
        message: r.message ?? r.body ?? "",
        error: r.error || null,
        author: r?.author ?? i18n.t("whatsappManager.detail.unknown"),
        createdAt: r.createdAt,
        timeAgo: moment(r.createdAt).utcOffset(-5).fromNow(),
      }));

      setMessages(msgs);
    } catch (err) {
      toast.error(i18n.t("whatsappManager.toasts.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (value) => (!value ? "-" : moment(value).format("DD/MM/YYYY HH:mm"));

  const statusChip = (status, timeAgo) => {
    const map = {
      sent: { label: i18n.t("whatsappManager.status.sent"), color: "primary", bg: blue[200], dot: green[600], text: blue[900] },
      pending: { label: i18n.t("whatsappManager.status.pending"), color: "default", bg: orange[200], dot: orange[700], text: orange[900] },
      failed: { label: i18n.t("whatsappManager.status.failed"), color: "secondary", bg: red[200], dot: red[700], text: red[900] },
    };

    const conf = map[status] || map.pending;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, width: 210 }}>
        <Chip
          size="small"
          label={conf.label}
          color={conf.color}
          className={classes.statusChip}
          style={{ backgroundColor: conf.bg, color: conf.text, fontWeight: 600 }}
        />
        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: conf.dot }} />
        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 500 }}>
          {timeAgo}
        </Typography>
      </div>
    );
  };

  const priorityChip = (priority) => {
    const map = {
      high: { label: i18n.t("whatsappManager.priority.high"), style: { background: alpha(red[500], 0.16), color: red[400] } },
      medium: { label: i18n.t("whatsappManager.priority.medium"), style: { background: alpha(orange[500], 0.16), color: orange[400] } },
      low: { label: i18n.t("whatsappManager.priority.low"), style: { background: alpha(green[500], 0.16), color: green[400] } },
    };
    const conf = map[priority] || map.low;
    return <Chip size="small" label={conf.label} className={classes.priorityChip} style={conf.style} />;
  };

  const quickRange = (days) => {
    setDateFrom(moment().subtract(days, "days").format("YYYY-MM-DD"));
    setDateTo(moment().format("YYYY-MM-DD"));
  };

  const cards = useMemo(
    () => [
      { title: i18n.t("whatsappManager.cards.sent"), value: summary.sent, Icon: SendIcon, color: blue[600] },
      { title: i18n.t("whatsappManager.cards.pending"), value: summary.pending, Icon: HourglassEmptyIcon, color: orange[600] },
      { title: i18n.t("whatsappManager.cards.failed"), value: summary.failed, Icon: ErrorOutlineIcon, color: red[600] },
      { title: i18n.t("whatsappManager.cards.multimedia"), value: summary.multimedia, Icon: ImageIcon, color: orange[700] },
    ],
    [summary]
  );

  const clearFilters = async () => {
    setFilterWhatsapp("");
    setFilterNumber("");
    setFilterStatus("ALL");
    setFilterPriority("ALL");
    setFilterQuery("");
    await loadData();
  };

  const renderMessageWithTooltip = (text) => {
    const content = text || "";
    const truncated = content.length > 40 ? content.slice(0, 40) + "…" : content;
    return content.length > 40 ? (
      <Tooltip title={content} arrow>
        <span style={{ cursor: "help" }}>{truncated}</span>
      </Tooltip>
    ) : (
      <span>{truncated}</span>
    );
  };

  return (
    <MainContainer maxWidth={false}>
      <MainHeader title={i18n.t("whatsappManager.title")} />
      <Paper className={classes.mainPaper} elevation={0}>
        {}
        <Grid container spacing={2} className={classes.cardsWrap}>
          {cards.map(({ title, value, Icon, color }, idx) => (
            <Grid key={idx} size={{ xs: 6, sm: 3, md: 3 }}>
              <div className={classes.card}>
                <Icon className={classes.cardIcon} style={{ color }} />
                <Typography className={classes.cardTitle}>{title}</Typography>
                <Typography className={classes.cardNumber} style={{ color }}>{value}</Typography>
              </div>
            </Grid>
          ))}
        </Grid>

        {}
        <div className={classes.filtersBar}>
          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, sm: 3, md: 2 }}>
              <TextField type="date" label={i18n.t("whatsappManager.filters.from")} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} fullWidth variant="outlined" size="small" slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 2 }}>
              <TextField type="date" label={i18n.t("whatsappManager.filters.to")} value={dateTo} onChange={(e) => setDateTo(e.target.value)} fullWidth variant="outlined" size="small" slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <SelectConection value={filterWhatsapp} onChange={(e) => setFilterWhatsapp(e)} channel='whatsapp whatsapp_cloud' isConnected={null} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField label={i18n.t("whatsappManager.filters.number")} placeholder={i18n.t("whatsappManager.filters.numberPlaceholder")} value={filterNumber} onChange={(e) => setFilterNumber(e.target.value)} fullWidth variant="outlined" size="small" />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{i18n.t("whatsappManager.filters.status")}</InputLabel>
                <Select label={i18n.t("whatsappManager.filters.status")} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <MenuItem value="ALL">{i18n.t("whatsappManager.filters.all")}</MenuItem>
                  <MenuItem value="sent">{i18n.t("whatsappManager.status.sent")}</MenuItem>
                  <MenuItem value="pending">{i18n.t("whatsappManager.status.pending")}</MenuItem>
                  <MenuItem value="failed">{i18n.t("whatsappManager.status.failed")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{i18n.t("whatsappManager.filters.priority")}</InputLabel>
                <Select label={i18n.t("whatsappManager.filters.priority")} value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
                  <MenuItem value="ALL">{i18n.t("whatsappManager.filters.all")}</MenuItem>
                  <MenuItem value="high">{i18n.t("whatsappManager.priority.high")}</MenuItem>
                  <MenuItem value="medium">{i18n.t("whatsappManager.priority.medium")}</MenuItem>
                  <MenuItem value="low">{i18n.t("whatsappManager.priority.low")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField label={i18n.t("whatsappManager.filters.content")} placeholder={i18n.t("whatsappManager.filters.contentPlaceholder")} value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} fullWidth variant="outlined" size="small" />
            </Grid>

            <Grid style={{ textAlign: "right" }} size={{ xs: 12, md: 6 }}>
              <Button className={classes.quickBtn} onClick={() => quickRange(0)}>{i18n.t("whatsappManager.filters.today")}</Button>
              <Button className={classes.quickBtn} onClick={() => quickRange(7)}>{i18n.t("whatsappManager.filters.last7")}</Button>
              <Button className={classes.quickBtn} onClick={() => quickRange(30)}>{i18n.t("whatsappManager.filters.last30")}</Button>
              <Button variant="outlined" onClick={clearFilters} style={{ marginLeft: 8 }}>{i18n.t("whatsappManager.filters.clear")}</Button>
              <Button color="primary" variant="contained" onClick={loadData} style={{ marginLeft: 8 }}>{i18n.t("whatsappManager.filters.apply")}</Button>
            </Grid>
          </Grid>
        </div>

        {}
        <Paper className={classes.tablePaper} elevation={0}>
          <TableContainer className={classes.tableContainer}>
            {loading ? (
              <div className={classes.stickyProgress}><CircularProgress /></div>
            ) : messages.length === 0 ? (
              <div className={classes.emptyState}>
                <Typography variant="h6" gutterBottom>{i18n.t("whatsappManager.empty.title")}</Typography>
                <Typography variant="body2">{i18n.t("whatsappManager.empty.subtitle")}</Typography>
              </div>
            ) : (
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell}>{i18n.t("whatsappManager.table.origin")}</TableCell>
                    <TableCell className={classes.headCell}>{i18n.t("whatsappManager.table.destination")}</TableCell>
                    <TableCell className={classes.headCell}>{i18n.t("whatsappManager.table.status")}</TableCell>
                    <TableCell className={classes.headCell}>{i18n.t("whatsappManager.table.priority")}</TableCell>
                    <TableCell className={classes.headCell} style={{ width: "10%" }}>{i18n.t("whatsappManager.table.date")}</TableCell>
                    <TableCell className={classes.headCell} style={{ width: "40%" }}>{i18n.t("whatsappManager.table.message")}</TableCell>
                    <TableCell className={classes.headCell} align="right">{i18n.t("whatsappManager.table.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.map((row, i) => (
                    <TableRow key={i} className={`${classes.rowHover} ${classes.zebra}`}>
                      <TableCell>{row.whatsapp}</TableCell>
                      <TableCell>+{row.number}</TableCell>
                      <TableCell>{statusChip(row.status, row.timeAgo)}</TableCell>
                      <TableCell>{priorityChip(row.priority)}</TableCell>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textPrimary">
                          {renderMessageWithTooltip(row.message)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={i18n.t("whatsappManager.table.viewDetails")}>
                          <IconButton size="small" onClick={() => openDetails(row)}>
                            <VisibilityOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        {(row.status === "failed" || row.error) ? (
                          <Tooltip title={i18n.t("whatsappManager.table.retry")}>
                            <span>
                              <IconButton size="small" onClick={() => handleRetry(row)} disabled={retryingId === row.id}>
                                <SendIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {!loading && messages.length > 0 && <Divider />}
        </Paper>
      </Paper>
      <Dialog open={detailOpen} onClose={closeDetails} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("whatsappManager.detail.title")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textPrimary" style={{ whiteSpace: "pre-wrap" }}>
            <strong>{i18n.t("whatsappManager.detail.number")}:</strong> {selectedRow?.number}
            {"\n"}<strong>{i18n.t("whatsappManager.detail.author")}:</strong> {selectedRow?.author ?? i18n.t("whatsappManager.detail.unknown")}
            {"\n"}<strong>{i18n.t("whatsappManager.detail.whatsapp")}:</strong> {selectedRow?.whatsapp}
            {"\n"}<strong>{i18n.t("whatsappManager.detail.date")}:</strong> {formatDate(selectedRow?.createdAt)}
            {"\n"}<strong>{i18n.t("whatsappManager.detail.status")}:</strong> {selectedRow?.status}
            {"\n"}<strong>{i18n.t("whatsappManager.detail.priority")}:</strong> {selectedRow?.priority}
            {"\n"}<strong>{i18n.t("whatsappManager.detail.detail")}:</strong> {selectedRow?.error || i18n.t("whatsappManager.detail.noError")}
            {"\n"}<strong>{i18n.t("whatsappManager.detail.message")}:</strong> {selectedRow?.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>{i18n.t("whatsappManager.detail.close")}</Button>
          <Button color="primary" variant="contained" onClick={() => selectedRow && handleRetry(selectedRow)} disabled={retryingId === selectedRow?.id}>
            {i18n.t("whatsappManager.detail.retry")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default WhatsappManager;
